/**
 * Utilitarios de normalización, formateo y filtrado de fechas
 * Garantiza consistencia total entre zona horaria local del navegador y strings almacenados en DB.
 */

/**
 * Normaliza cualquier formato de fecha a 'YYYY-MM-DD' en la zona horaria local del usuario.
 * Soporta:
 * - 'YYYY-MM-DD' o 'YYYY/MM/DD'
 * - 'DD/MM/YYYY' o 'DD-MM-YYYY'
 * - ISO timestamps ('2026-09-07T18:18:36.597Z', '2026-09-07 18:18:36+00', etc.)
 */
export function normalizeDateToYYYYMMDD(rawDate: string | undefined | null): string {
    if (!rawDate) return '';
    const clean = rawDate.trim();
    if (!clean) return '';

    // 1. Caso 'YYYY-MM-DD' estricto sin hora (evita desfases de Date UTC)
    const yyyymmddMatch = clean.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
    if (yyyymmddMatch) {
        const year = yyyymmddMatch[1];
        const month = yyyymmddMatch[2].padStart(2, '0');
        const day = yyyymmddMatch[3].padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // 2. Caso 'DD/MM/YYYY' o 'DD-MM-YYYY' estricto sin hora
    const ddmmyyyyMatch = clean.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (ddmmyyyyMatch) {
        const day = ddmmyyyyMatch[1].padStart(2, '0');
        const month = ddmmyyyyMatch[2].padStart(2, '0');
        const year = ddmmyyyyMatch[3];
        return `${year}-${month}-${day}`;
    }

    // 3. Fallback para ISO strings o timestamps con hora:
    // Extrae año, mes y día LOCALES del navegador, coincidiendo al 100% con toLocaleDateString()
    try {
        const d = new Date(clean);
        if (!isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    } catch {
        return '';
    }

    return '';
}

/**
 * Convierte una fecha de input 'YYYY-MM-DD' a un timestamp ISO ubicado a mediodía local (12:00:00).
 * Esto evita desfases de día (off-by-one) provocados por conversiones a UTC en cualquier huso horario (UTC-11 a UTC+12).
 */
export function dateInputToISO(dateStr: string | undefined | null): string {
    if (!dateStr) return new Date().toISOString();
    const norm = normalizeDateToYYYYMMDD(dateStr);
    if (!norm) return new Date().toISOString();

    const parts = norm.split('-');
    if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const localNoon = new Date(year, month, day, 12, 0, 0);
        if (!isNaN(localNoon.getTime())) {
            return localNoon.toISOString();
        }
    }
    return new Date().toISOString();
}

/**
 * Convierte 'YYYY-MM-DD' a formato visual español 'DD/MM/YYYY'.
 */
export function formatDateDisplay(dateStr: string | undefined | null): string {
    if (!dateStr) return '';
    const norm = normalizeDateToYYYYMMDD(dateStr);
    if (!norm) return dateStr;
    const parts = norm.split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
}

/**
 * Genera el texto para el chip de filtro activo de rango de fechas.
 */
export function formatDateRangeLabel(range: { start?: string; end?: string } | null | undefined): string {
    if (!range) return '';
    const startNorm = range.start ? normalizeDateToYYYYMMDD(range.start) : '';
    const endNorm = range.end ? normalizeDateToYYYYMMDD(range.end) : '';

    if (startNorm && endNorm) {
        if (startNorm === endNorm) {
            return `Fecha: ${formatDateDisplay(startNorm)}`;
        }
        return `${formatDateDisplay(startNorm)} - ${formatDateDisplay(endNorm)}`;
    }
    if (startNorm) {
        return `Desde ${formatDateDisplay(startNorm)}`;
    }
    if (endNorm) {
        return `Hasta ${formatDateDisplay(endNorm)}`;
    }
    return '';
}

/**
 * Rangos preestablecidos rápidos (Hoy, Esta semana, Este mes, Últimos 30 días)
 */
export function getDatePresets(referenceDate: Date = new Date()): {
    today: { start: string; end: string };
    thisWeek: { start: string; end: string };
    thisMonth: { start: string; end: string };
    last30Days: { start: string; end: string };
} {
    const todayStr = normalizeDateToYYYYMMDD(referenceDate.toISOString());

    // Esta semana (Lunes a Domingo)
    const day = referenceDate.getDay(); // 0 = Domingo, 1 = Lunes, ...
    const diffToMonday = (day === 0 ? -6 : 1) - day;
    const monday = new Date(referenceDate);
    monday.setDate(referenceDate.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const weekStart = normalizeDateToYYYYMMDD(monday.toISOString());
    const weekEnd = normalizeDateToYYYYMMDD(sunday.toISOString());

    // Este mes (1 al último día del mes)
    const firstDay = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
    const lastDay = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0);
    const monthStart = normalizeDateToYYYYMMDD(firstDay.toISOString());
    const monthEnd = normalizeDateToYYYYMMDD(lastDay.toISOString());

    // Últimos 30 días
    const past30 = new Date(referenceDate);
    past30.setDate(referenceDate.getDate() - 30);
    const past30Start = normalizeDateToYYYYMMDD(past30.toISOString());

    return {
        today: { start: todayStr, end: todayStr },
        thisWeek: { start: weekStart, end: weekEnd },
        thisMonth: { start: monthStart, end: monthEnd },
        last30Days: { start: past30Start, end: todayStr }
    };
}

/**
 * Comprueba si un parte de trabajo coincide con el filtro de fechas activo.
 * Regla de Oro:
 * - Prioriza la fecha visible del parte (scheduledDate o en su defecto createdAt).
 * - NO produce falsos positivos emparejando createdAt si scheduledDate tiene otro valor.
 * - Incluye jornadas de laborJournal si el técnico trabajó en esa fecha.
 * - En vista de archivo, incluye completedAt.
 */
export function isOrderInDateRange(
    order: {
        scheduledDate?: string | null;
        createdAt?: string | null;
        completedAt?: string | null;
        laborJournal?: Array<{ date?: string | null }>;
    },
    dateRange: { start?: string; end?: string } | null | undefined,
    options: boolean | { isArchiveView?: boolean; fallbackToCreatedAt?: boolean } = false
): boolean {
    if (!dateRange) return true;
    const start = dateRange.start ? normalizeDateToYYYYMMDD(dateRange.start) : '';
    const end = dateRange.end ? normalizeDateToYYYYMMDD(dateRange.end) : '';
    if (!start && !end) return true;

    const isArchiveView = typeof options === 'boolean' ? options : Boolean(options?.isArchiveView);
    const fallbackToCreatedAt = typeof options === 'boolean' ? true : (options?.fallbackToCreatedAt ?? true);

    const datesToCheck: string[] = [];

    // 1. Fecha visible principal de la orden (scheduledDate o en su defecto createdAt si no hay jornadas)
    if (order.scheduledDate) {
        const norm = normalizeDateToYYYYMMDD(order.scheduledDate);
        if (norm) datesToCheck.push(norm);
    } else if (order.laborJournal && order.laborJournal.length > 0) {
        // Tiene jornadas en el diario: las fechas del parte son las jornadas, no createdAt
    } else if (fallbackToCreatedAt && order.createdAt) {
        const norm = normalizeDateToYYYYMMDD(order.createdAt);
        if (norm) datesToCheck.push(norm);
    }

    // 2. Jornadas de trabajo registradas en el diario
    if (order.laborJournal && order.laborJournal.length > 0) {
        order.laborJournal.forEach(j => {
            if (j.date) {
                const norm = normalizeDateToYYYYMMDD(j.date);
                if (norm) datesToCheck.push(norm);
            }
        });
    }

    // 3. Fecha de finalización (solo relevante en el archivo histórico)
    if (isArchiveView && order.completedAt) {
        const norm = normalizeDateToYYYYMMDD(order.completedAt);
        if (norm) datesToCheck.push(norm);
    }

    if (datesToCheck.length === 0) return false;

    return datesToCheck.some(d => {
        if (start && end) return d >= start && d <= end;
        if (start) return d >= start;
        if (end) return d <= end;
        return true;
    });
}
